<template>
    <v-card :title="pgettext('label', 'Patient Transport')">
        <v-card-text>
            <Form ref="form"
                  @submit="submit"
                  class="form form__control hpo-patient-modal-form"
                  :validation-schema="schema">

                <!--                    <button class="button red-button" @click="prefillData">-->
                <!--                        HROMADNÝ ODSUN-->
                <!--                    </button>-->

                <!-- DIAGNOSE -->
                <NewFormGroup inputId="diagnose" name="diagnose"
                              :label="pgettext('label', 'Diagnose')">
                    <div class="d-flex ga-4 align-center flex-column-reverse flex-md-row">
                        <v-btn @click="showDiagnosisBuilder = true">
                            {{ pgettext('action', 'Enter Diagnosis') }}
                        </v-btn>
                        <Field type="text" name="diagnose" id="diagnose" v-model="patient.diagnose"/>
                    </div>
                </NewFormGroup>

                <!-- UPV -->
                <NewFormGroup inputId="ventilated" name="ventilated" :required="true"
                              :label="pgettext('label', 'ALS')">
                    <Field type="hidden" name="ventilated" id="ventilated" v-model="patient.ventilated"/>
                    <div class="phc-buttons-row">
                        <SelectButton :value="YesNo.YES" v-model="patient.ventilated">
                            {{ YesNoLabels[YesNo.YES] }}
                        </SelectButton>
                        <SelectButton :value="YesNo.NO" v-model="patient.ventilated">
                            {{ YesNoLabels[YesNo.NO] }}
                        </SelectButton>
                    </div>
                </NewFormGroup>

                <!-- UNIT TYPE -->
                <NewFormGroup inputId="unitType" name="unitType" :required="true"
                              :label="pgettext('label', 'Unit Type')">
                    <Field type="hidden" name="unitType" id="unitType" v-model="patient.unitType"/>
                    <div class="phc-buttons-row">
                        <SelectButton v-for="[value, label] of Object.entries(UnitTypeLabel)"
                                      :value="value"
                                      v-model="patient.unitType">
                            {{ label }}
                        </SelectButton>
                    </div>
                </NewFormGroup>

                <!-- UNIT TYPE SPEC -->
                <NewFormGroup v-if="parseInt(patient.unitType) === UnitType.OTHER" :required="true"
                              inputId="unitTypeSpec" name="unitTypeSpec"
                              :label="pgettext('label', 'Unit Type')">
                    <Field type="text" name="unitTypeSpec" id="unitTypeSpec" v-model="patient.unitTypeSpec"/>
                </NewFormGroup>


                <!-- UNIT CALL SIGN / LP -->
                <NewFormGroup inputId="callSign" name="callSign" :required="true"
                              :label="pgettext('label', 'Call sing / LP')">
                    <Field type="text" name="callSign" id="callSign" v-model="patient.callSign"/>
                </NewFormGroup>

                <!-- HOSPITAL TYPE -->
                <NewFormGroup inputId="hospitalType" name="hospitalType" :required="true"
                              :label="pgettext('label', 'Hospital Type')">

                    <div class="phc-buttons-row">
                        <SelectButton v-for="ward of suggestedWards"
                                      :value="ward"
                                      v-model="patient.hospitalType">
                            {{ ward }}
                        </SelectButton>
                    </div>
                    <Field type="text" name="hospitalType" id="hospitalType"
                           v-model="patient.hospitalType"
                           placeholder="Jiné - napište zde"/>
                </NewFormGroup>

                <v-row no-gutters>
                    <v-col cols="12">
                        <v-btn v-if="transportTerminatable"
                               color="red"
                               type="submit"
                               :loading="loading"
                               :text="pgettext('action', 'Cancel Transport')"
                               block
                        ></v-btn>
                        <template v-else>
                            <v-btn color="green"
                                   type="submit"
                                   @click="setStatusDraft"
                                   :loading="loading"
                                   :text="pgettext('action', 'Save Draft')"
                                   block
                            ></v-btn>

                            <v-btn type="submit"
                                   :loading="loading"
                                   :text="pgettext('action', 'Submit Transport Request')"
                                   block
                            ></v-btn>
                        </template>
                    </v-col>
                </v-row>
            </Form>

            <DiagnosisBuilderDialog v-model="showDiagnosisBuilder"
                                    :diagnosis="patient.diagnose"
                                    @save="saveDiagnosis"/>
        </v-card-text>
    </v-card>
</template>

<script>
import {ref} from "vue";
import {ErrorMessage, Field, Form} from "vee-validate";
import {oneOfNumberValidator, requiredMessage, stringValidator} from "@/utils/yupValidators";

import Dropdown from "@/components/forms/Dropdown";

import NewFormGroup from "@/components/forms/NewFormGroup"
import RadioGroup from "@/components/forms/RadioGroup";
import {phcButtonClass} from "../utils";
import {UnitType, UnitTypeLabel, YesNo, YesNoLabels} from "../../../../choices";
import * as yup from "yup";
import {useMutation} from "@vue/apollo-composable";
import {HpoPatientTransportMutation} from "@/graphql/mutations/hpo/patient/transport.graphql";
import {handleDjangoForm} from "@/utils/forms";
import SelectButton from "../SelectButton.vue";
import {HpoTransportStatus} from "../../choices";
import DiagnosisBuilderDialog from "@/components/hpo/patientModal/diagnosis/DiagnosisBuilderDialog.vue";


yup.addMethod(yup.string, 'containsDigit', function containsDigit(value) {
    return this.test(`test-contains-digit`, gettext('Must contain letters and digits'), function (value) {
        return /\d/.test(value) && /[A-Za-z]/.test(value)
    });
});

export default {
    name: 'TransportTab',
    components: {
        DiagnosisBuilderDialog,
        Form,
        Field,
        ErrorMessage,
        NewFormGroup,
        Dropdown,
        RadioGroup,
        SelectButton,
    },
    emits: ['close', 'setActiveTab', 'submitTransport'],
    props: {
        hpoId: {
            type: String,
            required: true
        },
        patient: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const patient = ref({...props.patient})
        const status = ref(HpoTransportStatus.DISP_PENDING)

        const suggestedWards = ['EMER', 'TRAUMACENTRUM', 'CHIR', 'INT']

        const schema = ref(yup.object({
            diagnose: stringValidator.required(requiredMessage),
            ventilated: oneOfNumberValidator(YesNo),
            unitType: oneOfNumberValidator(UnitType),
            unitTypeSpec: yup.string().nullable().when('unitType', {
                is: (val) => val === UnitType.OTHER,
                then: () => {
                    return stringValidator.required(requiredMessage)
                }
            }),
            callSign: stringValidator.required(requiredMessage).uppercase().when('unitType', {
                is: (val) => [UnitType.RV, UnitType.RZP, UnitType.RLP].includes(val),
                then: () => {
                    return yup.string().containsDigit()
                }
            }),
            hospitalType: stringValidator.required(requiredMessage),
            status: stringValidator
        }))

        const {mutate, loading} = useMutation(HpoPatientTransportMutation);

        const showDiagnosisBuilder = ref(false);
        function saveDiagnosis({diagnosis}) {
            patient.value.diagnose = diagnosis;
            showDiagnosisBuilder.value = false;
        }

        return {
            // Imports
            phcButtonClass,
            UnitType,
            UnitTypeLabel,
            YesNo,
            YesNoLabels,
            HpoTransportStatus,

            // Variables
            patient,
            schema,
            suggestedWards,
            status,
            showDiagnosisBuilder,

            // Functions
            saveDiagnosis,

            // GQL
            mutate,
            loading,
        }
    },
    watch: {
        'patient.unitType': {
            handler: function (newType, previousType) {
                newType = parseInt(newType)
                previousType = parseInt(previousType)
                const prefix = 'ZPM '
                const emsVehicles = [UnitType.RV, UnitType.RZP, UnitType.RLP]
                if (emsVehicles.includes(newType)) {
                    if (!emsVehicles.includes(previousType)) {
                        if (!this.patient.callSign || !this.patient.callSign.startsWith(prefix)) {
                            this.patient.callSign = prefix
                        }
                    }
                } else {
                    if (emsVehicles.includes(previousType)) {
                        if (this.patient.callSign.startsWith(prefix)) {
                            this.patient.callSign = null
                        }
                    }
                }
            }
        }
    },
    computed: {
        transportTerminatable() {
            return [
                HpoTransportStatus.DISP_PENDING,
                HpoTransportStatus.HOSPITAL_PENDING,
                HpoTransportStatus.READY,
                HpoTransportStatus.ON_WAY
            ].includes(this.patient.transportStatus)
        }
    },
    methods: {
        prefillData() {
            if (confirm(gettext('Submit group transport?'))) {
                this.submit({
                    callSign: "autobus",
                    diagnose: "lehká poranění",
                    hospitalType: "CHIR",
                    unitType: 6,
                    unitTypeSpec: null,
                    ventilated: 0,
                })
            }
        },
        setStatusDraft() {
            this.status = HpoTransportStatus.DRAFT
        },
        setStatusTerminated() {
            this.status = HpoTransportStatus.TERMINATED
        },
        submit(values) {
            this.mutate({
                hpoId: this.hpoId,
                patientId: this.patient.id,
                status: this.status,
                ...values
            }).then(
                handleDjangoForm(this.$refs.form)
            ).then(result => {
                if (this.status !== HpoTransportStatus.TERMINATED) {
                    this.$emit('submitTransport');
                }
                this.$emit('close')
            }).catch((error) => {

            })
        }
    }
}

</script>

